import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Trash2, ExternalLink, Clock, Building, AlertTriangle, CheckCircle2, Loader2, Scale, XCircle, Plus, Eye, ArrowRight, Webhook, Info, Link as LinkIcon } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Spinner,
} from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ApiErrorDisplay } from '@/components/features/error';
import { ServiceForm, DeleteConfirmation, PaymentPagePreview, PreviewDeviceToggle } from '@/components/features/services';
import { TransactionForm } from '@/components/features/transactions/transaction-form';
import { useService, useUpdateService, useDeleteService } from '@/hooks/use-services';
import { useWebhookConfig } from '@/hooks/use-webhook-config';
import { formatDate, formatCents } from '@/lib/utils';
import type { UpdateServiceRequest } from '@/types/service';
import { THEME_LAYOUTS } from '@/types/service';
import { getCarrierLogoLabel } from '@/lib/utils/carrier-logos';
import type { DeviceMode } from '@/components/features/services';

/**
 * Service detail page.
 * Route: /services/:id
 *
 * Shows all service details and provides edit/delete actions.
 */
export function ServiceDetailPage() {
  const { t } = useTranslation('services');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Spec 0027: merchantId from route-state (passed by service-card navigate)
  const merchantId = (location.state as { merchantId?: string })?.merchantId;

  // Data fetching
  const { data: service, isLoading, error, refetch } = useService(id ?? '', merchantId);

  // Mutations
  const updateMutation = useUpdateService();
  const deleteMutation = useDeleteService();

  // Webhook config for status display
  const { data: webhookConfig } = useWebhookConfig(service?.merchantId ?? '');

  // Dialog state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [txCreateOpen, setTxCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<DeviceMode>('mobile');

  // Handlers
  const handleUpdate = (serviceId: string, data: UpdateServiceRequest) => {
    updateMutation.mutate(
      { serviceId, data },
      {
        onSuccess: () => {
          setEditFormOpen(false);
        },
      }
    );
  };

  const handleApproveLegalLinks = () => {
    if (!service) return;
    updateMutation.mutate({ serviceId: service.serviceId, data: { legalLinksStatus: 'approved' } });
  };

  const handleRejectLegalLinks = () => {
    if (!service) return;
    updateMutation.mutate(
      { serviceId: service.serviceId, data: { legalLinksStatus: 'rejected' } },
      { onSuccess: () => setRejectConfirmOpen(false) }
    );
  };

  const handleDeleteConfirm = () => {
    if (!service) return;
    deleteMutation.mutate(service.serviceId, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        navigate('/services');
      },
    });
  };

  if (!id) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('detail.missingId')}</p>
        <Button variant="link" onClick={() => navigate('/services')}>
          {t('common:button.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!service) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('detail.notFound')}</p>
        <Button variant="link" onClick={() => navigate('/services')}>
          {t('common:button.back')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{service.description}</h1>
            <Badge variant={service.status === 'active' ? 'success' : 'secondary'}>
              {service.status === 'active' ? t('common:status.active') : t('common:status.inactive')}
            </Badge>
          </div>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{service.serviceId}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/services')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common:button.back')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditFormOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t('common:button.edit')}
          </Button>
          {service.status === 'active' && (
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('common:button.deactivate')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.basicInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.merchant')}</p>
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono">{service.merchantId}</span>
              </div>
            </div>

            {/* Spec 0128: Chain vs. Legacy gatewayServiceId */}
            {service.fallbackChain && service.fallbackChain.length > 0 ? (
              <div>
                <p className="text-sm text-muted-foreground">Gateway-Service-ID (Legacy)</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-muted-foreground line-through">{service.gatewayServiceId}</p>
                  <Badge variant="outline" className="text-xs">deprecated</Badge>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">{t('form.label.gatewayServiceId')}</p>
                <p className="font-mono">{service.gatewayServiceId}</p>
              </div>
            )}

            {service.facadeServiceId && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.facadeServiceId')}</p>
                <p className="font-mono">{service.facadeServiceId}</p>
              </div>
            )}

            <div>
              <p className="text-sm text-muted-foreground">{t('form.label.description')}</p>
              <p>{service.description}</p>
            </div>

            {/* Spec 0128: msisdnCollectionUrl */}
            {service.fallbackChain && service.fallbackChain.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground">MSISDN-Erfassung</p>
                {service.msisdnCollectionUrl ? (
                  <div className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-muted-foreground" />
                    <a href={service.msisdnCollectionUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-blue-600 hover:underline">
                      {service.msisdnCollectionUrl}
                    </a>
                  </div>
                ) : (
                  <p className="text-sm">Facade-Standard</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Spec 0128: Fallback-Chain Card */}
        {service.fallbackChain && service.fallbackChain.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fallback-Kette</CardTitle>
              <CardDescription>{service.fallbackChain.length} Einträge</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {service.fallbackChain.map((entry, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between rounded-md border p-2 ${index === 0 ? 'border-primary/40 bg-primary/5' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-muted-foreground w-5">#{index + 1}</span>
                      {index === 0 && <Badge variant="secondary" className="text-xs">Primär</Badge>}
                      <Badge variant="outline">{entry.flowType}</Badge>
                      <span className="font-mono text-sm">{entry.gatewayServiceId}</span>
                    </div>
                    {entry.iqServiceId && (
                      <span className="font-mono text-xs text-muted-foreground">{entry.iqServiceId}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Gateway-Service</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-2 rounded-md border border-blue-500/50 bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950/20 dark:text-blue-200">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p>Dieser Service nutzt das alte Format mit einzelner Gateway-Service-ID.</p>
                  <p className="mt-1 font-mono text-xs">{service.gatewayServiceId}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.pricing')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.amount')}</p>
              <p className="text-2xl font-bold">{formatCents(service.grossAmount)}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">{t('detail.currency')}</p>
              <p>{service.currency}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">{t('form.label.subscriptionInterval')}</p>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{t(`interval.${service.subscriptionInterval}`)}</span>
                <span className="text-xs text-muted-foreground">({service.subscriptionInterval})</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Theme Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('detail.themeSettings')}</CardTitle>
              {service.themeStatus === 'provisioning' && (
                <Badge variant="outline" className="gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('detail.themeStatusProvisioning')}
                </Badge>
              )}
              {service.themeStatus === 'ready' && (
                <Badge variant="success" className="gap-1.5">
                  <CheckCircle2 className="h-3 w-3" />
                  {t('detail.themeStatusReady')}
                </Badge>
              )}
              {service.themeStatus === 'failed' && (
                <Badge variant="warning" className="gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  {t('detail.themeStatusFailed')}
                </Badge>
              )}
            </div>
            <CardDescription>{t('detail.themeDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.buttonTextLabel')}</p>
              <p>{t(`buttonText.${String(service.theme?.buttonTextId ?? 1)}`)}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">{t('detail.themeLayout')}</p>
              <p>
                {(() => {
                  const layout = service.themeLayout ?? 'default';
                  return (THEME_LAYOUTS as readonly string[]).includes(layout)
                    ? t(`form.option.themeLayout.${layout}`)
                    : layout;
                })()}
              </p>
            </div>

            {service.theme?.stylesheetUrl && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.stylesheet')}</p>
                <a
                  href={service.theme.stylesheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="truncate text-sm">{service.theme.stylesheetUrl}</span>
                </a>
              </div>
            )}

            {service.theme?.headerImageUrl && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.headerImage')}</p>
                <a
                  href={service.theme.headerImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="truncate text-sm">{service.theme.headerImageUrl}</span>
                </a>
              </div>
            )}

            {service.theme?.backgroundImageUrl && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.backgroundImage')}</p>
                <a
                  href={service.theme.backgroundImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="truncate text-sm">{service.theme.backgroundImageUrl}</span>
                </a>
              </div>
            )}

            {service.theme?.benefitText && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.benefitText')}</p>
                <p className="text-sm">{service.theme.benefitText}</p>
              </div>
            )}

            {service.theme?.complianceCarrierLogoId !== undefined && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.carrierLogo')}</p>
                <p className="text-sm">
                  {getCarrierLogoLabel(service.theme.complianceCarrierLogoId) ?? '—'}{' '}
                  <span className="text-muted-foreground">({service.theme.complianceCarrierLogoId})</span>
                </p>
              </div>
            )}

            {service.themeStatus === 'failed' && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('detail.themeStatusRetryHint')}
                </p>
              </div>
            )}

            {!service.theme?.stylesheetUrl &&
              !service.theme?.headerImageUrl &&
              !service.theme?.backgroundImageUrl &&
              !service.theme?.benefitText &&
              service.theme?.complianceCarrierLogoId === undefined && (
                <p className="text-sm text-muted-foreground">{t('detail.noCustomAssets')}</p>
              )}
          </CardContent>
        </Card>

        {/* Legal Links Card (Spec 0040 M-5) — nur anzeigen wenn LegalLinks vorhanden */}
        {service.legalLinks && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{t('legalLinks.title')}</CardTitle>
                {service.legalLinksStatus === 'pending_review' && (
                  <Badge variant="warning" className="gap-1.5">
                    <Scale className="h-3 w-3" />
                    {t('legalLinks.pending')}
                  </Badge>
                )}
                {service.legalLinksStatus === 'approved' && (
                  <Badge variant="success" className="gap-1.5">
                    <CheckCircle2 className="h-3 w-3" />
                    {t('legalLinks.approved')}
                  </Badge>
                )}
                {service.legalLinksStatus === 'rejected' && (
                  <Badge variant="destructive" className="gap-1.5">
                    <XCircle className="h-3 w-3" />
                    {t('legalLinks.rejected')}
                  </Badge>
                )}
              </div>
              <CardDescription>{t('legalLinks.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(['agbUrl', 'withdrawalUrl', 'imprintUrl', 'privacyUrl', 'supportUrl'] as const).map((field) => (
                <div key={field}>
                  <p className="text-sm text-muted-foreground">{t(`legalLinks.${field}`)}</p>
                  {service.legalLinks?.[field] ? (
                    <a
                      href={service.legalLinks[field]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="truncate text-sm">{service.legalLinks[field]}</span>
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">{t('legalLinks.notSet')}</p>
                  )}
                </div>
              ))}

              {service.legalLinksStatus !== 'approved' && (
                <p className="text-sm text-muted-foreground">{t('legalLinks.defaultHint')}</p>
              )}

              {/* Approval Buttons — nur bei pending_review */}
              {service.legalLinksStatus === 'pending_review' && (
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleApproveLegalLinks}
                    disabled={updateMutation.isPending}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t('legalLinks.approve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setRejectConfirmOpen(true)}
                    disabled={updateMutation.isPending}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    {t('legalLinks.reject')}
                  </Button>
                </div>
              )}

              {/* Reject Confirm Dialog */}
              {rejectConfirmOpen && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
                  <p className="mb-2 text-sm text-red-800 dark:text-red-200">{t('legalLinks.rejectConfirm')}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="destructive" onClick={handleRejectLegalLinks} disabled={updateMutation.isPending}>
                      {t('legalLinks.reject')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectConfirmOpen(false)}>
                      {t('common:button.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Timestamps Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.timestamps')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.createdAt')}</p>
              <p>{formatDate(service.createdAt)}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">{t('detail.updatedAt')}</p>
              <p>{formatDate(service.updatedAt)}</p>
            </div>

            {service.syncedAt && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.syncedAt')}</p>
                <p>{formatDate(service.syncedAt)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* TX Create Button */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={() => setTxCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('detail.createTransaction')}
        </Button>
        <Button variant="outline" onClick={() => setPreviewOpen(true)}>
          <Eye className="mr-2 h-4 w-4" />
          {t('detail.previewPaymentPage')}
        </Button>
      </div>

      {/* Related Data Links */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">{t('detail.quickLinks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/transactions?serviceId=${service.serviceId}`)}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              {t('detail.viewTransactions')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/subscriptions?serviceId=${service.serviceId}`)}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              {t('detail.viewSubscriptions')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/webhooks/config')}
            >
              <Webhook className="mr-2 h-4 w-4" />
              {t('detail.viewWebhookConfig')}
              {webhookConfig?.webhookUrl && (
                <Badge variant="success" className="ml-2">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  OK
                </Badge>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* TX Create Dialog */}
      <Dialog open={txCreateOpen} onOpenChange={setTxCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{t('detail.createTransaction')}</DialogTitle>
            <DialogDescription>{t('detail.createTransactionDesc')}</DialogDescription>
          </DialogHeader>
          <TransactionForm
            lockedServiceId={service.serviceId}
            embedded
            onSuccess={() => {
              setTxCreateOpen(false);
              navigate('/transactions');
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Page Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle>{t('detail.previewPaymentPage')}</DialogTitle>
              <PreviewDeviceToggle mode={previewDevice} onChange={setPreviewDevice} />
            </div>
            <DialogDescription>{t('detail.previewPaymentPageDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <PaymentPagePreview
              description={service.description}
              grossAmount={service.grossAmount}
              subscriptionInterval={service.subscriptionInterval}
              theme={service.theme}
              deviceMode={previewDevice}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Form Dialog */}
      <ServiceForm
        service={service}
        open={editFormOpen}
        onOpenChange={setEditFormOpen}
        onSubmitUpdate={handleUpdate}
        isSubmitting={updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        service={service}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
